'use client'

import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Upload } from 'lucide-react'

export type UploadedImage = {
  url: string
  thumb: string
  caption?: string
}

export type ImageUploaderRef = {
  uploadFile: (file: File) => Promise<UploadedImage | null>
}

interface Props {
  sellerId: string | number
  onUploaded: (image: UploadedImage) => void
  endpoint?: string // opcional por si quieres reusar en otras rutas
}

export const ImageUploader = forwardRef<ImageUploaderRef, Props>(function ImageUploader(
  { sellerId, onUploaded, endpoint = '/api/image-manager/upload' },
  ref
) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)

  async function upload(file: File): Promise<UploadedImage | null> {
    const form = new FormData()
    form.append('file', file)
    form.append('sellerId', String(sellerId))

    setUploading(true)

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        body: form,
      })

      const json = await res.json()

      if (json?.image) {
        onUploaded(json.image)
        return json.image as UploadedImage
      }

      return null
    } finally {
      setUploading(false)
    }
  }

  useImperativeHandle(ref, () => ({
    uploadFile: upload,
  }))

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) upload(file)
        }}
      />

      <Button variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()}>
        <Upload className="h-4 w-4 mr-2" />
        {uploading ? 'Subiendo…' : 'Subir imagen'}
      </Button>
    </div>
  )
})
